
/**
 * Uygulama genelindeki tüm veri doğrulama mantığı.
 */
export const Validator = {
  fullName: (name: string): { isValid: boolean; normalized: string; error: string | null } => {
    const trimmed = name.trim()
    if (trimmed.length < 3)
      return { isValid: false, normalized: trimmed, error: 'Ad Soyad en az 3 karakter olmalıdır' }
    const regex = /^[\p{L}\s.']{3,50}$/u
    if (!regex.test(trimmed))
      return { isValid: false, normalized: trimmed, error: 'Lütfen geçerli bir isim giriniz' }
    const normalized = trimmed
      .split(/\s+/)
      .map(
        word => word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1).toLocaleLowerCase('tr-TR')
      )
      .join(' ')
    return { isValid: true, normalized, error: null }
  },

  iban: (iban: string, currency: string = 'TRY'): { isValid: boolean; formatted: string | null; error: string | null } => {
    if (!iban) return { isValid: false, formatted: null, error: 'Hesap bilgisi boş olamaz' }
    const cleaned = iban.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
    
    if (currency === 'TRY') {
      if (cleaned.length < 15) return { isValid: false, formatted: null, error: 'IBAN çok kısa' }
      if (!cleaned.startsWith('TR'))
        return { isValid: false, formatted: null, error: 'IBAN "TR" ile başlamalıdır' }
      if (cleaned.length !== 26)
        return { isValid: false, formatted: null, error: "Türkiye IBAN'ı 26 karakter olmalıdır" }
      const trIbanRegex = /^TR\d{24}$/
      if (!trIbanRegex.test(cleaned))
        return { isValid: false, formatted: null, error: 'Geçersiz IBAN formatı' }
      if (/^TR0{24}$/.test(cleaned) || /^TR1{24}$/.test(cleaned)) {
        return { isValid: false, formatted: null, error: 'Lütfen geçerli bir hesap numarası giriniz' }
      }
      const rearranged = cleaned.slice(4) + cleaned.slice(0, 4)
      const numeric = rearranged
        .split('')
        .map(char => {
          const code = char.charCodeAt(0)
          return code >= 65 && code <= 90 ? (code - 55).toString() : char
        })
        .join('')
      try {
        const remainder = BigInt(numeric) % 97n
        if (remainder !== 1n)
          return { isValid: false, formatted: null, error: 'Geçersiz IBAN (Checksum hatası)' }
      } catch (_e) {
        return { isValid: false, formatted: null, error: 'Hesaplama hatası' }
      }
      const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned
      return { isValid: true, formatted, error: null }
    } else {
      // Non-TRY: Cüzdan, hesap vb.
      if (cleaned.length < 5) return { isValid: false, formatted: null, error: 'Hesap numarası çok kısa' }
      if (cleaned.length > 50) return { isValid: false, formatted: null, error: 'Hesap numarası çok uzun' }
      
      const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned
      return { isValid: true, formatted, error: null }
    }
  },

  phone: (phone: string): { isValid: boolean; normalized: string | null; error: string | null } => {
    if (!phone) return { isValid: false, normalized: null, error: 'Telefon numarası boş olamaz' }
    let cleaned = phone.replace(/[^\d]/g, '')
    if (cleaned.startsWith('90')) cleaned = cleaned.slice(2)
    if (cleaned.startsWith('0')) cleaned = cleaned.slice(1)
    if (/^(\d)\1{9}$/.test(cleaned))
      return { isValid: false, normalized: null, error: 'Geçersiz telefon numarası' }
    const regex = /^5\d{9}$/
    if (!regex.test(cleaned))
      return {
        isValid: false,
        normalized: null,
        error: 'Geçerli bir telefon numarası giriniz (5xx...)',
      }
    return { isValid: true, normalized: '+90' + cleaned, error: null }
  },

  formatPhone: (phone: string | null): string => {
    if (!phone) return ''
    let cleaned = phone.replace(/[^\d]/g, '')
    if (cleaned.startsWith('90')) cleaned = cleaned.slice(2)
    else if (cleaned.startsWith('0')) cleaned = cleaned.slice(1)
    if (cleaned.length !== 10) return phone
    return `+90 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8, 10)}`
  },

  email: (email: string): { isValid: boolean; error: string | null } => {
    if (!email) return { isValid: false, error: 'E-posta adresi boş olamaz' }
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    if (!regex.test(email)) return { isValid: false, error: 'Geçerli bir e-posta adresi giriniz' }
    return { isValid: true, error: null }
  },

  sanitize: (input: string): string => {
    if (!input) return ''
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }
    return input
      .replace(/<[^>]*>?/gm, '')
      .replace(/[&<>"']/g, m => map[m] || m)
      .trim()
  },
}

/**
 * Alan Bazlı Yetkilendirme Motoru
 */
export const PermissionManager = {
  canUpdateField: (user: any | null, field: string): boolean => {
    if (!user) return false
    const provider = user.app_metadata?.provider || user.identities?.[0]?.provider
    switch (field) {
      case 'email':
        return !(provider === 'google' || provider === 'apple')
      case 'full_name':
      case 'phone':
      default:
        return true
    }
  },

  getPrimaryIdentity: (user: any | null): string => {
    if (!user) return 'unknown'
    return user.app_metadata?.provider || user.identities?.[0]?.provider || 'email'
  },
}

/**
 * IBAN formatını doğrular
 * @deprecated Validator.iban() kullanın
 */
export function validateIBAN(iban: string, currency: string = 'TRY') {
  return Validator.iban(iban, currency)
}

/**
 * Kullanıcının onaylı üye kriterlerini karşılayıp karşılamadığını hesaplar.
 */
export function calculateUserVerification(user: any | null): {
  isVerified: boolean
  reasons: string[]
  score: number
} {
  if (!user) return { isVerified: false, reasons: ['Kullanıcı bulunamadı'], score: 0 }
  const reasons: string[] = []
  let score = 0
  if (user.avatar_url || user.google_avatar_url || user.apple_avatar_url) {
    score += 20
  } else {
    reasons.push('Profil fotoğrafı eksik')
  }
  if (user.full_name && user.full_name.trim().length >= 5) {
    score += 20
  } else {
    reasons.push('Ad soyad yetersiz')
  }
  if (user.ibans && user.ibans.length > 0) {
    score += 30
  } else {
    reasons.push('Geçerli bir IBAN hesabı tanımlanmamış')
  }
  if (user.phone_number && !Validator.phone(user.phone_number).isValid) {
    reasons.push('Telefon numarası formatı geçersiz')
  }
  const reputation = user.reputation_score || 0
  if (reputation >= 90) {
    score += 30
  } else {
    reasons.push('İtibar puanı düşük (Min: 90)')
  }
  return {
    isVerified: score === 100,
    reasons,
    score,
  }
}
